import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { useAuth } from '../../context/AuthContext';

// Profile → Sign-in & security: lets a Google-signed-in leader set a direct password
// (works on networks that block the OAuth redirect); password accounts get the rotate link.
export const SignInSecurityCard = () => {
  const navigate = useNavigate();
  const { user, setPassword } = useAuth();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const isGoogle = user?.auth_provider === 'google';
  const hasPassword = !!user?.has_password;

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await setPassword(pw);
      setPw(''); setConfirm('');
      toast.success('Password set — you can now sign in with your email and password, or Google.');
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="signin-security-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-teal-600" /> Sign-in &amp; security</CardTitle>
        <CardDescription>Sessions stay signed in for up to 30 days, then you are asked to sign in again.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 text-xs" data-testid="signin-methods">
          {isGoogle && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-slate-700" data-testid="signin-method-google"><ShieldCheck className="h-3.5 w-3.5" /> Google sign-in</span>}
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${hasPassword ? 'bg-teal-50 text-teal-800' : 'bg-amber-50 text-amber-800'}`} data-testid="signin-method-password">
            <KeyRound className="h-3.5 w-3.5" /> {hasPassword ? 'Email + password enabled' : 'No password set'}
          </span>
        </div>

        {hasPassword ? (
          <Button variant="outline" onClick={() => navigate('/change-password')} data-testid="change-password-btn">Change password</Button>
        ) : (
          <form onSubmit={submit} className="space-y-3 max-w-md" data-testid="set-password-form">
            <p className="text-sm text-slate-600">Set a direct password so you can sign in on networks that block the Google redirect. Google sign-in keeps working too.</p>
            <div>
              <Label className="text-sm font-medium text-slate-700">New password</Label>
              <div className="relative mt-1">
                <Input type={show ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} minLength={8} required className="pr-10" data-testid="set-password-input" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="set-password-toggle">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">At least 8 characters with a letter and a number or symbol.</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Confirm password</Label>
              <Input type={show ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1" data-testid="set-password-confirm-input" />
            </div>
            <Button type="submit" disabled={busy || pw.length < 8} className="bg-teal-600 hover:bg-teal-700" data-testid="set-password-submit">
              {busy ? 'Saving…' : 'Set password'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default SignInSecurityCard;
